/**
 * Ring tones synthesised with the WebAudio API (no external audio asset). Two
 * cadences: an attention-grabbing two-tone incoming ring (with vibration on
 * mobile browsers) and a softer outgoing ringback.
 *
 * Autoplay note: a browser may keep the AudioContext suspended until a user
 * gesture. The incoming ring can arrive with no prior gesture, so audio may be
 * silent until the user interacts. Vibration and the visual modal are the
 * primary incoming cues. This is verified per browser during dogfood.
 */

class RingTone {
    constructor() {
        this.ctx = null;
        this.toneInterval = null;
        this.vibrateInterval = null;
    }

    _ensureContext() {
        if (!this.ctx) {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) {
                return null;
            }
            this.ctx = new AudioCtx();
        }
        if (this.ctx.state === 'suspended') {
            this.ctx.resume().catch(() => {});
        }
        return this.ctx;
    }

    _burst(frequencies, duration) {
        const ctx = this.ctx;
        if (!ctx) {
            return;
        }
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.14, now + 0.05);
        gain.gain.setValueAtTime(0.14, now + Math.max(0.06, duration - 0.05));
        gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
        gain.connect(ctx.destination);

        frequencies.forEach((freq) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            osc.connect(gain);
            osc.start(now);
            osc.stop(now + duration);
        });
    }

    startIncoming() {
        this.stop();
        const ctx = this._ensureContext();
        if (ctx) {
            const ring = () => {
                this._burst([440, 480], 0.4);
                setTimeout(() => this._burst([440, 480], 0.4), 600);
            };
            ring();
            this.toneInterval = setInterval(ring, 3000);
        }
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            const pattern = [400, 200, 400, 1600];
            navigator.vibrate(pattern);
            this.vibrateInterval = setInterval(() => navigator.vibrate(pattern), 3000);
        }
    }

    startOutgoing() {
        this.stop();
        const ctx = this._ensureContext();
        if (ctx) {
            const ring = () => this._burst([400], 1.0);
            ring();
            this.toneInterval = setInterval(ring, 3000);
        }
    }

    stop() {
        if (this.toneInterval) {
            clearInterval(this.toneInterval);
            this.toneInterval = null;
        }
        if (this.vibrateInterval) {
            clearInterval(this.vibrateInterval);
            this.vibrateInterval = null;
        }
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(0);
        }
    }
}

export const ringTone = new RingTone();

export default ringTone;
